/**
 * Отраслевые профили — определяют лексику, критерии скоринга и секции отчёта.
 * Тип отчёта (startup/investment/…) задаёт цель решения, отрасль — предметный контекст.
 */

export const RESEARCH_INDUSTRIES = {
  it_digital: {
    label: "IT и цифровые сервисы",
    description: "ПО, SaaS, маркетплейсы, AI, автоматизация, телеком",
    keywords: [
      "saas", "software", "приложен", "it ", "айти", "цифров", "облак",
      "api", "платформ", "стартап", "b2b", "crm", "erp", "маркетплейс",
      "искусственн", "llm", "нейросет",
    ],
    competitorLabel: "продукт / сервис",
    pricingLabel: "тарифы и модели монетизации",
    includeSeo: true,
    includeForeignPlayers: true,
    includeActionPack: true,
    criteriaLabels: {
      market_size: "Объём рынка (TAM)",
      market_growth: "Рост рынка",
      demand_signal: "Спрос / SEO",
      entry_opportunity: "Возможность входа",
      monetization: "Монетизация",
      competition_pressure: "Конкуренция (10 = слабая)",
    },
    analystRole: "аналитик B2B-рынков цифровых продуктов в РФ",
    focusBlock: `Используй язык цифровых продуктов: SaaS, подписка, ARPU, интеграции, воронка.
Конкуренты — сервисы и платформы. pricing_benchmarks.model: «Подписка», «Лицензия», «Freemium», «Enterprise».`,
    avoidTerms: [] as string[],
  },
  medicine: {
    label: "Медицина и здоровье",
    description: "Клиники, медизделия, фарма, лаборатории, telemedicine",
    keywords: [
      "медиц", "клиник", "стоматолог", "фарм", "здравоохран", "врач",
      "больниц", "диагност", "лаборатор", "медуслуг", "дмс", "омс",
      "медиздел", "регистрационн", "медтех",
    ],
    competitorLabel: "клиника / оператор / производитель",
    pricingLabel: "чек, прайс услуг, возмещение",
    includeSeo: true,
    includeForeignPlayers: true,
    includeActionPack: false,
    criteriaLabels: {
      market_size: "Объём сегмента (пациенты / выручка)",
      market_growth: "Рост спроса на услуги",
      demand_signal: "Спрос (записи, очереди, поиск)",
      entry_opportunity: "Барьеры входа (лицензии, Минздрав)",
      monetization: "Модель оплаты (ОМС/ДМС/прямая)",
      competition_pressure: "Плотность игроков (10 = мало конкурентов)",
    },
    analystRole: "аналитик рынка медицинских услуг и healthcare в РФ",
    focusBlock: `НЕ используй SaaS, PMF, MRR, лендинг как обязательную модель.
Фокус: лицензии и аккредитация, клинические протоколы, ОМС/ДМС, регистрация медизделий,
география клиник, врачебный персонал, цикл записи пациента, репутация и регуляторика.
Конкуренты — клиники, сети, производители, дистрибьюторы. Цены — чек, прайс-лист, возмещение.`,
    avoidTerms: ["SaaS", "PMF", "MRR", "product-market fit", "лендинг", "подписка B2B"],
  },
  construction: {
    label: "Строительство",
    description: "Генподряд, отделка, проектирование, материалы, девелопмент",
    keywords: [
      "строит", "строй", "ремонт", "отделк", "подряд", "генподряд",
      "девелоп", "жиль", "коттедж", "смет", "снип", "гост", "проектирован",
      "фасад", "кровл", "инженерн", "монтаж",
    ],
    competitorLabel: "подрядчик / застройщик",
    pricingLabel: "смета, ₽/м², авансирование",
    includeSeo: true,
    includeForeignPlayers: false,
    includeActionPack: false,
    criteriaLabels: {
      market_size: "Объём сегмента (м² / ₽)",
      market_growth: "Динамика ввода / спроса",
      demand_signal: "Заявки, тендеры, поиск подрядчиков",
      entry_opportunity: "Допуски СРО, оборотка, кадры",
      monetization: "Маржа и схема оплаты (аванс/этапы)",
      competition_pressure: "Плотность подрядчиков (10 = мало)",
    },
    analystRole: "аналитик строительного и подрядного рынка в РФ",
    focusBlock: `НЕ используй SaaS, подписку, digital-продукт как основную модель.
Фокус: субподряд, сметы, нормативы (СНиП/ГОСТ/СП), допуски СРО, сезонность,
логистика материалов, обеспечение заявками, гарантийные обязательства, регион.
Конкуренты — подрядчики, застройщики, поставщики материалов. Цены — ₽/м², смета, аванс.`,
    avoidTerms: ["SaaS", "SaaS в стройке", "PMF", "MRR", "API", "product_architecture"],
  },
  mechanical_engineering: {
    label: "Машиностроение",
    description: "Оборудование, станки, комплектующие, сервисное обслуживание",
    keywords: [
      "машиностро", "станк", "оборудован", "комплектующ", "металлообработ",
      "чпу", "cnc", "пресс", "насос", "двигател", "редуктор", "арматур",
      "промышленн", "завод",
    ],
    competitorLabel: "производитель / интегратор",
    pricingLabel: "цена единицы, сервисный контракт",
    includeSeo: true,
    includeForeignPlayers: true,
    includeActionPack: false,
    criteriaLabels: {
      market_size: "Объём сегмента оборудования",
      market_growth: "Рост инвестиций / модернизации",
      demand_signal: "Спрос (тендеры, импортозамещение)",
      entry_opportunity: "Сертификация, производственные мощности",
      monetization: "Маржа, сервис, запчасти",
      competition_pressure: "Конкуренция OEM (10 = слабая)",
    },
    analystRole: "аналитик рынка промышленного оборудования в РФ",
    focusBlock: `Фокус: импортозамещение, сертификация, цикл закупки B2B, ТО и запчасти,
производственные мощности, дилерские сети, ГОЗ/тендеры при релевантности.
Конкуренты — заводы, дистрибьюторы, интеграторы. Не навязывай SaaS.`,
    avoidTerms: ["SaaS", "лендинг", "PMF", "воронка активации"],
  },
  manufacturing: {
    label: "Производство",
    description: "Выпуск продукции, контрактное производство, FMCG, лёгкая промышленность",
    keywords: [
      "производ", "завод", "фабрик", "выпуск", "контрактн", "fmcg",
      "пищев", "лёгк", "химическ", "упаков", "сырь", "цех", "линия",
    ],
    competitorLabel: "производитель",
    pricingLabel: "себестоимость, опт, маржа",
    includeSeo: true,
    includeForeignPlayers: true,
    includeActionPack: false,
    criteriaLabels: {
      market_size: "Объём производства / рынка сбыта",
      market_growth: "Рост потребления",
      demand_signal: "Спрос дистрибьюторов / B2B",
      entry_opportunity: "CAPEX, сертификация, сырьё",
      monetization: "Маржа, каналы сбыта",
      competition_pressure: "Конкуренция производителей",
    },
    analystRole: "аналитик производственных ниш в РФ",
    focusBlock: `Фокус: мощности, сырьё и логистика, сертификация (ЕАС, ХАССП и т.д.),
каналы дистрибуции, сезонность, контрактное vs собственное производство.
Конкуренты — заводы и бренды. Цены — опт, себестоимость, наценка дистрибьютора.`,
    avoidTerms: ["SaaS", "MRR", "product_architecture", "лендинг"],
  },
  trade_sales: {
    label: "Торговля и продажи",
    description: "Опт/розница, дистрибуция, франшиза, отделы продаж",
    keywords: [
      "продаж", "торгов", "розниц", "опт", "дистриб", "франшиз",
      "дилер", "ритейл", "e-commerce", "маркетплейс продав", "отдел продаж",
      "комиссион", "склад", "обув", "кроссов", "ботинк", "туфл", "сникер",
      "одежд", "аппарел", "fashion",
    ],
    competitorLabel: "продавец / дистрибьютор",
    pricingLabel: "наценка, закуп, ретро-бонус",
    includeSeo: true,
    includeForeignPlayers: true,
    includeActionPack: true,
    criteriaLabels: {
      market_size: "Объём категории",
      market_growth: "Рост категории",
      demand_signal: "Спрос (поиск, маркетплейсы)",
      entry_opportunity: "Барьеры (закуп, бренды, логистика)",
      monetization: "Маржа и оборачиваемость",
      competition_pressure: "Конкуренция продавцов",
    },
    analystRole: "аналитик торговли и дистрибуции в РФ",
    focusBlock: `Фокус: закупочные цены, наценка, оборачиваемость, полочное пространство,
логистика, франшиза vs собственная сеть, маркетплейсы vs офлайн.
Конкуренты — федеральные сети и крупные бренды категории (для обуви: Эконика, Kari, Rendez-Vous, Zenden, Henderson и др.), дистрибьюторы, маркетплейс-селлеры.
Не ограничивайся только «прямыми аналогами» бренда из темы — включай топ рынка по выручке/числу точек.`,
    avoidTerms: ["SaaS как основная модель", "product_architecture"],
  },
  general: {
    label: "Общая тематика",
    description: "Автоопределение по теме, без отраслевого шаблона",
    keywords: [] as string[],
    competitorLabel: "игрок рынка",
    pricingLabel: "ценообразование",
    includeSeo: true,
    includeForeignPlayers: true,
    includeActionPack: true,
    criteriaLabels: {
      market_size: "Объём рынка",
      market_growth: "Рост рынка",
      demand_signal: "Сигнал спроса",
      entry_opportunity: "Возможность входа",
      monetization: "Монетизация",
      competition_pressure: "Конкуренция (10 = слабая)",
    },
    analystRole: "аналитик рынков в РФ",
    focusBlock: `Подбери предметную лексику под тему пользователя. Не навязывай IT/SaaS, если тема не цифровая.`,
    avoidTerms: [] as string[],
  },
} as const;

export type ResearchIndustry = keyof typeof RESEARCH_INDUSTRIES;

export const RESEARCH_INDUSTRY_VALUES = Object.keys(
  RESEARCH_INDUSTRIES,
) as ResearchIndustry[];

/** Выбор в форме создания: enum отрасли или свой вариант («Другое»). `auto` — legacy. */
export const RESEARCH_INDUSTRY_INPUT = [
  ...RESEARCH_INDUSTRY_VALUES,
  "custom",
  "auto",
] as const;

export type ResearchIndustryInput = (typeof RESEARCH_INDUSTRY_INPUT)[number];

export const RESEARCH_INDUSTRY_INPUT_VALUES = [
  ...RESEARCH_INDUSTRY_VALUES,
  "custom",
  "auto",
] as unknown as [string, ...string[]];

/** @deprecated используйте RESEARCH_INDUSTRY_INPUT */
export const RESEARCH_INDUSTRY_WITH_AUTO = RESEARCH_INDUSTRY_INPUT;

/** @deprecated используйте RESEARCH_INDUSTRY_INPUT_VALUES */
export const RESEARCH_INDUSTRY_WITH_AUTO_VALUES = RESEARCH_INDUSTRY_INPUT_VALUES;

export function isResearchIndustry(value: string): value is ResearchIndustry {
  return value in RESEARCH_INDUSTRIES;
}

export function getIndustryLabel(industry?: string | null): string {
  if (industry && isResearchIndustry(industry)) {
    return RESEARCH_INDUSTRIES[industry].label;
  }
  return RESEARCH_INDUSTRIES.general.label;
}

export function getIndustryProfile(industry?: string | null) {
  if (industry && isResearchIndustry(industry)) {
    return RESEARCH_INDUSTRIES[industry];
  }
  return RESEARCH_INDUSTRIES.general;
}

/** Быстрая эвристика до LLM-классификации */
export function guessIndustryFromTopic(topic: string): ResearchIndustry {
  const lower = topic.toLowerCase();

  let best: ResearchIndustry = "general";
  let bestScore = 0;

  for (const [key, profile] of Object.entries(RESEARCH_INDUSTRIES)) {
    if (key === "general") continue;
    const score = profile.keywords.reduce((acc, keyword) => {
      return lower.includes(keyword) ? acc + 1 : acc;
    }, 0);
    if (score > bestScore) {
      bestScore = score;
      best = key as ResearchIndustry;
    }
  }

  return bestScore > 0 ? best : "general";
}

export function buildIndustryCriteria(industry: ResearchIndustry) {
  const profile = RESEARCH_INDUSTRIES[industry];
  const weights = [0.2, 0.15, 0.2, 0.15, 0.15, 0.15] as const;
  const keys = Object.keys(profile.criteriaLabels) as Array<
    keyof typeof profile.criteriaLabels
  >;

  return keys.map((key, index) => ({
    key,
    label: profile.criteriaLabels[key],
    weight: weights[index] ?? 0.15,
  }));
}
